import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';
import { CSSTransition } from 'react-transition-group';
import {
  Typography,
  Link,
  Container,
  Card,
  CardActionArea,
  CardContent,
  IconButton,
} from '@mui/material';

import useInsuranceInfo from '../../hooks/useInsuranceInfo';
import useLocalStorage from '../../hooks/useLocalStorage';
import useVitoloNPSSurvey from './useVitoloNPSSurvey';
import useShowOnboarding from './useShowOnboarding';
import useShowContributionConfirmationDocumentNotification from './useShowContributionConfirmationDocumentNotification';
import useIdentifyHotjarUser from './useIdentifyHotjarUser';

import ClaimStatusNotifications from '../../components/ClaimStatusNotifications';
import { Notification, PageTitle } from '../../components/common';
import { GET_CLAIMS, GET_CUSTOMER, useReady } from '../../shared';
import ErrorPlaceholder from '../errorPlaceholder';

import { StyledNotificationText } from '../../components/styled/Notification.styled';
import { showRimburseMap, showSelfCancellationMap } from '../../utils/mappings';

import CaretRightIcon from '../../icons/CaretRightIcon';
import EuroIcon from '../../icons/EuroIcon';
import DataIcon from '../../icons/DataIcon';
import DocumentIcon from '../../icons/DocumentIcon';
import PaymentCardIcon from '../../icons/PaymentCardIcon';
import CurrentlySelectedContractCard from './CurrentlySelectedContractCard/CurrentlySelectedContractCard';
import LinkToClaimsPage from './LinkToClaimsPage';
import PromotionCarousel from '../promotions/PromotionCarousel';
import InsureAnotherPetBanner from '../../components/Banners/InsureAnotherPetBanner/InsureAnotherPetBanner';
import MissingIBANCard from '../MissingIBANCard';
import CareDocument from '../../components/PatoloCareDocument';

import {
  StyledDashboardCardContent,
  StyledDashboardCardsContainer,
  StyledDashboardCardTextContent,
  StyledNotificationsContainer,
  StyledVerticalContent,
} from './Dashboard.styled';

const renderLink = (chunk, href) => (
  <Link variant="p" href={href}>
    {chunk}
  </Link>
);

export default function Dashboard() {
  const ready = useReady();
  const history = useHistory();
  const intl = useIntl();

  const [getFromLocalStorage, saveToLocalStorage] = useLocalStorage();

  const [missingIban, setMissingIban] = useState(true);

  const { type: insuranceType, isPatolo } = useInsuranceInfo();

  const { data: customerData, loading: customerLoading } = useQuery(GET_CUSTOMER);
  const { digitalPaymentMethod, customer } = customerData || {};
  const { iban, key, uuid } = customer || {};

  const { data: claimsData, loading: claimsLoading } = useQuery(GET_CLAIMS);

  const { active: hasActiveDigitalPaymentMethod } = digitalPaymentMethod || {};

  const contractNotCancelled = !customer?.contract?.cancelationDate;

  useEffect(() => {
    const isContractMissingIbanExistsInStorage = getFromLocalStorage(
      `customer-missing-iban-${key}`
    );

    setMissingIban(isContractMissingIbanExistsInStorage);
  }, [getFromLocalStorage, key]);

  useIdentifyHotjarUser(uuid);

  useVitoloNPSSurvey();

  useShowOnboarding();

  const { showCCDocumentNotification, setCCDocumentSeen, dismissCCDocumentNotification } =
    useShowContributionConfirmationDocumentNotification();

  // TODO: dismissDentalMissingIban when the story of dentolo is ready
  const dismissMissingIbanNotification = () => {
    saveToLocalStorage(`customer-missing-iban-${key}`, true);
    setMissingIban(true);
  };

  const showReimbursementCard = showRimburseMap[`${insuranceType}`];
  const showContractCancelLink = showSelfCancellationMap[`${insuranceType}`];

  return (
    <>
      <Container>
        <StyledNotificationsContainer>
          <ClaimStatusNotifications />

          {!!showCCDocumentNotification && (
            <Notification type="success" onDismiss={dismissCCDocumentNotification}>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <Link
                variant="nostyle"
                component="button"
                onClick={() => setCCDocumentSeen().then(() => history.push('/payment-overview'))}
              >
                <StyledNotificationText $title>
                  <Typography variant="h3">
                    <FormattedMessage id="payment.overview.notification.title" />
                  </Typography>
                </StyledNotificationText>

                <StyledNotificationText>
                  <Typography variant="p">
                    <FormattedMessage id="payment.overview.notification.text" />
                  </Typography>
                </StyledNotificationText>
              </Link>
            </Notification>
          )}
        </StyledNotificationsContainer>

        {!customerLoading && customer?.contract && (
          <>
            <CSSTransition in={ready} timeout={600} classNames="slow-fade" unmountOnExit>
              <>
                <PageTitle>
                  <Typography variant="h1">
                    <FormattedMessage id="dashbaord.title" />
                  </Typography>
                </PageTitle>

                <StyledVerticalContent>
                  <CurrentlySelectedContractCard />

                  {!iban && hasActiveDigitalPaymentMethod && !missingIban && (
                    <MissingIBANCard onDismiss={dismissMissingIbanNotification} />
                  )}

                  {!iban && hasActiveDigitalPaymentMethod && missingIban && <MissingIBANCard />}

                  {showReimbursementCard &&
                    !claimsLoading &&
                    Array.isArray(claimsData.claims) &&
                    claimsData.claims.length <= 0 && <LinkToClaimsPage />}

                  <StyledDashboardCardsContainer>
                    <Card elevation={1}>
                      <CardActionArea
                        aria-label={intl.formatMessage({
                          id: 'dashboard.cards.claims.title',
                        })}
                        onClick={() => history.push('/user-claims-info')}
                      >
                        <CardContent>
                          <StyledDashboardCardContent>
                            <EuroIcon />

                            <StyledDashboardCardTextContent>
                              <Typography variant="h3">
                                <FormattedMessage id="dashboard.cards.claims.title" />
                              </Typography>

                              <Typography variant="p">
                                <FormattedMessage id="dashboard.cards.claims.content" />
                              </Typography>
                            </StyledDashboardCardTextContent>

                            <IconButton
                              variant="round"
                              size="medium"
                              onClick={(e) => {
                                e.stopPropagation();
                                history.push('/user-claims-info');
                              }}
                              aria-label={intl.formatMessage({
                                id: 'dashboard.cards.claims.title',
                              })}
                            >
                              <CaretRightIcon />
                            </IconButton>
                          </StyledDashboardCardContent>
                        </CardContent>
                      </CardActionArea>
                    </Card>

                    <Card elevation={1}>
                      <CardActionArea
                        aria-label={intl.formatMessage({
                          id: 'dashboard.cards.personal_data.title',
                        })}
                        onClick={() => history.push('/user-info')}
                      >
                        <CardContent>
                          <StyledDashboardCardContent>
                            <DataIcon />

                            <StyledDashboardCardTextContent>
                              <Typography variant="h3">
                                <FormattedMessage id="dashboard.cards.personal_data.title" />
                              </Typography>
                            </StyledDashboardCardTextContent>

                            <IconButton
                              variant="round"
                              size="medium"
                              onClick={(e) => {
                                e.stopPropagation();
                                history.push('/user-info');
                              }}
                              aria-label={intl.formatMessage({
                                id: 'dashboard.cards.personal_data.title',
                              })}
                            >
                              <CaretRightIcon />
                            </IconButton>
                          </StyledDashboardCardContent>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </StyledDashboardCardsContainer>

                  {/* hide carousel for patolo contract */}
                  {!isPatolo && <PromotionCarousel />}

                  <StyledDashboardCardsContainer>
                    <Card elevation={1}>
                      <CardActionArea
                        aria-label={intl.formatMessage({
                          id: 'dashboard.cards.contract_documents.title',
                        })}
                        onClick={() => history.push('/contract-docs')}
                      >
                        <CardContent>
                          <StyledDashboardCardContent>
                            <DocumentIcon />

                            <StyledDashboardCardTextContent>
                              <Typography variant="h3">
                                <FormattedMessage id="dashboard.cards.contract_documents.title" />
                              </Typography>

                              <Typography variant="p">
                                <FormattedMessage id="dashboard.cards.contract_documents.content" />
                              </Typography>
                            </StyledDashboardCardTextContent>

                            <IconButton
                              variant="round"
                              size="medium"
                              onClick={(e) => {
                                e.stopPropagation();
                                history.push('/contract-docs');
                              }}
                              aria-label={intl.formatMessage({
                                id: 'dashboard.cards.contract_documents.title',
                              })}
                            >
                              <CaretRightIcon />
                            </IconButton>
                          </StyledDashboardCardContent>
                        </CardContent>
                      </CardActionArea>
                    </Card>

                    <Card elevation={1}>
                      <CardActionArea
                        aria-label={intl.formatMessage({
                          id: 'dashboard.cards.payment_data.title',
                        })}
                        onClick={() => history.push('/user-info#payment-info')}
                      >
                        <CardContent>
                          <StyledDashboardCardContent>
                            <PaymentCardIcon />

                            <StyledDashboardCardTextContent>
                              <Typography variant="h3">
                                <FormattedMessage id="dashboard.cards.payment_data.title" />
                              </Typography>

                              <Typography variant="p">
                                <FormattedMessage id="dashboard.cards.payment_data.content" />
                              </Typography>
                            </StyledDashboardCardTextContent>

                            <IconButton
                              variant="round"
                              size="medium"
                              onClick={(e) => {
                                e.stopPropagation();
                                history.push('/user-info#payment-info');
                              }}
                              aria-label={intl.formatMessage({
                                id: 'dashboard.cards.payment_data.title',
                              })}
                            >
                              <CaretRightIcon />
                            </IconButton>
                          </StyledDashboardCardContent>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </StyledDashboardCardsContainer>

                  {isPatolo && <CareDocument />}

                  {showContractCancelLink && contractNotCancelled && (
                    <Typography variant="p">
                      <FormattedMessage
                        id="policy.cancellation.text"
                        values={{
                          a: (chunk) => renderLink(chunk, '/contract-cancellation'),
                        }}
                      />
                    </Typography>
                  )}
                </StyledVerticalContent>
              </>
            </CSSTransition>

            {!isPatolo && <InsureAnotherPetBanner customer={customer} />}
          </>
        )}
      </Container>

      {!customerLoading && !customer?.contract && <ErrorPlaceholder />}
    </>
  );
}
